import React, { useEffect, useRef, useState } from "react";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Reusable message thread. Polls every 15s so we get near-live updates.
 * Phase 5 replaces polling with websocket / socket.io.
 */
export default function MessageThread({ conversationId, otherPartyName, otherPartyAvatar, participants, compact = false }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    if (!conversationId) return;
    try {
      const { data } = await api.get(`/messages/conversations/${conversationId}`);
      setMessages(data.messages || []);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  useEffect(() => {
    if (conversationId) {
      api.post(`/messages/conversations/${conversationId}/read`).catch(() => {});
    }
  }, [conversationId, messages.length]);

  const send = async () => {
    const b = body.trim();
    if (!b || !conversationId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/conversations/${conversationId}`, { body: b });
      setMessages((prev) => [...prev, data]);
      setBody("");
    } catch (err) {
      toast.error("Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`flex flex-col rounded-2xl border border-slate-200 bg-white ${compact ? "h-[420px]" : "h-[520px]"}`} data-testid="message-thread">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <div className="h-9 w-9 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center">
          <MessageSquare className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-[#071b22] truncate">{otherPartyName || "Conversation"}</div>
          <div className="text-xs text-[#252A34]/60">All discussion stays on this thread.</div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="message-list">
        {messages.length === 0 ? (
          <div className="text-center py-16 text-sm text-[#252A34]/50">Start the conversation — say hi.</div>
        ) : (
          messages.map((m) => <Bubble key={m.id} msg={m} me={user?.id} />)
        )}
      </div>
      <div className="border-t border-slate-100 p-3 flex items-end gap-2">
        <textarea
          rows={1}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message…"
          className="flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-[#165a71] focus:bg-white"
          data-testid="message-input"
        />
        <button
          disabled={sending || !body.trim()}
          onClick={send}
          className="rounded-full bg-[#165a71] text-white h-10 w-10 flex items-center justify-center hover:bg-[#11485a] disabled:opacity-40"
          data-testid="message-send-btn"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function Bubble({ msg, me }) {
  const isMine = msg.sender_id === me;
  if (msg.kind === "system") {
    return (
      <div className="my-3 text-center">
        <div className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-[11px] uppercase tracking-wider text-[#252A34]/60" data-testid="system-message">
          {msg.body}
        </div>
      </div>
    );
  }
  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`} data-testid={`message-bubble-${isMine ? "mine" : "them"}`}>
      <div translate="no" className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm ${
        isMine ? "bg-[#165a71] text-white" : "bg-slate-100 text-[#071b22]"
      }`}>
        {msg.body}
      </div>
    </div>
  );
}
